import axios, { AxiosRequestConfig } from 'axios'
import { $AxE2Config } from './$AxE2Config'
import { $lsCache } from './$lsCache'
import { $axAuthState, AxAuthStateManager } from './$axAuthStateManager'
import { axApiPaths } from '@/AxApiPaths'

// import { $errorReporter } from './$errorReporter'

/**
 * Ax認証TokenをLocalStorageに保存するKey名称
 */
const axUserClientIdLocalStorageKey = 'AxE2-UserClientIdLocalStorageKey'

const invalidValueList = [undefined, null]
const allowedValueList = [true, false]

export interface AxUserTokenDataStructure {
  access_token: string
  expires_in: number
  refresh_token: string
}
export interface AxTokenResponse {
  user_token: AxUserTokenDataStructure
  user: {
    id: string // "10"
  }
}

interface AxProxyResponseGeneralType<Result = any> {
  errors: any[]
  info: {
    api: { deprecate: null; version: 'v1.0' }
  }
  result: Result
  warnings: []
}

interface axProxyRequestArg {
  path: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  params?: any
  data?: {
    query?: {
      [paramName: string]: any
    }
    [paramName: string]: any
  }
}

const isAxProxyRequest = (config: AxiosRequestConfig) => config.params && config.params.axApiPath
const setAxTokenHeadersOnAxiosRequest = async (config) => {
  const addHeaders = {}
  if (config.baseURL === $AxE2Config.api.baseUrl) {
    addHeaders['X-Axc-Site-Name'] = $AxE2Config.ax.site_name
  }
  try {
    //if (isAxProxyRequest(config)) {
    // /clients/create および、 auth 関連を参照するときにはLoopしてしまうので、付与しない
    const axApiPath = config.params.axApiPath
    const isClientIdRequest = axApiPaths.clientId.create === axApiPath
    const axUserClientString = isClientIdRequest ? '' : await $axFrontendHelper.getAxUserClientId()
    let axTokenString = null
    let axUserId = null
    //let cloudKMemberId = null
    if (
      !axApiPath ||
      (axApiPath.indexOf('/auth') !== 0 && axApiPath !== axApiPaths.users.create && !isClientIdRequest)
    ) {
      axTokenString = await $axAuthState.currentAxUserAccessToken()
      axUserId = $axAuthState.userId
      //cloudKMemberId = $axAuthState.cloudKMemberId
    }

    if (axUserClientString) {
      addHeaders['X-Axc-User-Client-Id'] = axUserClientString
    }
    if (axTokenString) {
      addHeaders['X-Axc-User-Access-Token'] = axTokenString
    }
    if (axUserId) {
      addHeaders['X-Axc-User-Id'] = axUserId
    }
    /*
      if (cloudKMemberId) {
        addHeaders['X-CloudK-User-Id'] = cloudKMemberId
      }*/
    //}
  } catch (e) {
    // デバッグ用ログ
    console.error(e)
    // @ts-ignore
    // window.$nuxt.$sentry.captureException(e)
    return config
  } finally {
  }
  return {
    ...config,
    headers: {
      ...config.headers,
      ...addHeaders,
    },
  }
}
axios.interceptors.request.use(setAxTokenHeadersOnAxiosRequest)

/**
 * AxのAPIとの疎通を司るClass
 */
export class AxFrontendHelper {
  public cartDetail: any
  public $axAuthState: AxAuthStateManager
  public AxUserClientId: string
  public initialized: boolean

  constructor() {
    this.$axAuthState = $axAuthState
    this.cartDetail = {}
    this.initialized = false
    // clientIdの取得のため等、initする
    window.addEventListener('load', () => this.initOnLoad())
  }

  /**
   * Initialize
   */
  async initOnLoad() {
    await this.getAxUserClientId()
    // デバッグ用ログ
    // console.log(`async AxFrontendHelper.initOnLoad() executed.`)
    this.initialized = true
  }

  /**
   * AxUserClientIdを取得する。1日キャッシュするが、なければ取得する。
   * AxUserClientId = AxAPIと疎通する際に必須となる、ユーザ毎に発行するセッションキーのようなもの。
   */
  async getAxUserClientId(useCache = true) {
    if (!useCache) {
      const clientId = await this._fetchAxUserClientId()
      $lsCache.set(axUserClientIdLocalStorageKey, clientId)
      return clientId
    }
    return $lsCache.getWithFetchFunc(axUserClientIdLocalStorageKey, () => this._fetchAxUserClientId())
  }

  async _fetchAxUserClientId() {
    const res = await this.request<{ user_client_id: string }>({
      path: axApiPaths.clientId.create,
    })
    return res.result.user_client_id
  }

  /**
   * AxAPIへのリクエストを実施 (Proxy)
   */
  async request<Result = any>(
    args: axProxyRequestArg,
    { showErrorToast = true } = {}
  ): Promise<AxProxyResponseGeneralType<Result>> {
    try {
      return this._axApiProxyRequest<Result>(args, { showErrorToast })
    } catch (e) {
      throw e
    }
  }

  async requestToNormal(args: { path: string; method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; params?: any; data?: any }) {
    const { path, params, method, data } = args
    let axiosArg: AxiosRequestConfig = {
      baseURL: $AxE2Config.api.baseUrl,
      url: path,
      method,
      params: params || {},
    }

    if (data) {
      axiosArg.data = data
    }
    try {
      const res = await axios.request(axiosArg)
      return res.data
    } catch (e) {
      // デバッグ用ログ
      // console.log(`[$apiHelper.request()] Error on request:`, e)
      throw e
    }
  }

  async _axApiProxyRequest<Result = any>(
    { path, method = 'POST', params = null, data = null }: axProxyRequestArg,
    { showErrorToast = true } = {}
  ): Promise<AxProxyResponseGeneralType<Result>> {
    params = Object.assign({}, params || {}, { axApiPath: path })
    const axiosArg: AxiosRequestConfig = {
      baseURL: $AxE2Config.api.baseUrl,
      // url: '/axApiProxyRequest',
      url: $AxE2Config.api.axApiProxyPath,
      method,
      params,
      data,
      proxy: false,
    }
    console.log(axiosArg)
    try {
      const res = await axios.request(axiosArg)
      return res.data
    } catch (e) {
      // デバッグ用ログ（errorReporter、console.log）
      // $errorReporter.r(e, this)
      // console.log(`[AxFrontendHelper] Error on request:`, e)
      throw e
    }
  }

  /**
   * AxのAPIがかなりパラメータに煩いので、クリーンアップする。
   * ObjectでなければValueはすべてstringへ
   * (例: Optionalフィールドを空文字やnull受け付けない等)
   * @param data
   * @param allowedFields
   */
  cleanupObjectForRequest(data, allowedFields: (string | number)[] = null) {
    return (allowedFields || Object.keys(data)).reduce((res, key) => {
      if (invalidValueList.indexOf(data[key]) === -1) {
        const shouldNotConvert = typeof data[key] === 'object' || allowedValueList.indexOf(data[key]) !== -1
        res[key] = shouldNotConvert ? data[key] : `${data[key]}`
      }
      if (key.toString().indexOf('custom') > -1) {
        if (!res['custom']) {
          res['custom'] = {}
        }
        const custom_keyName = key.toString().replace('custom.', '')
        const shouldNotConvert =
          typeof data['custom'][custom_keyName] === 'object' ||
          allowedValueList.indexOf(data['custom'][custom_keyName]) !== -1
        res['custom'][custom_keyName] = shouldNotConvert
          ? data['custom'][custom_keyName]
          : `${data['custom'][custom_keyName]}`
      }
      // if(key.toString().indexOf('custom') > -1){
      //   if(!res['custom']){
      //     res['custom'] = {};
      //   }
      //   const custom_keyName = key.toString().replace("custom.","");
      //   const shouldNotConvert = typeof data[custom_keyName] === 'object' || allowedValueList.indexOf(data[custom_keyName]) !== -1
      //   res['custom'][custom_keyName] = shouldNotConvert ? data[custom_keyName] : `${data[custom_keyName]}`
      // }
      return res
    }, {})
  }
}

/**
 * AxApi Response の共通型定義
 */
interface AxProxyResponseGeneralType<Result = any> {
  errors: any[]
  info: {
    api: { deprecate: null; version: 'v1.0' }
  }
  result: Result
  warnings: []
}

export const $axFrontendHelper = new AxFrontendHelper()
